import { api } from "@/lib/api-client"

export type AdminTenderDerivedStatus = "processing" | "failed" | "completed" | "other"

export type AdminTenderTraceLink = {
  step: "extraction" | "bid-review"
  jsonUrl: string
  csvUrl: string
}

export type AdminTenderRunSummary = {
  caseId: string
  caseTitle: string
  caseStatus: string
  runId: string
  runNumber: number
  createdAt: string
  extractionStatus: string | null
  bidReviewStatus: string | null
  cvMatchStatus: string | null
  latestActivityAt: string | null
  derivedStatus: AdminTenderDerivedStatus
}

export type AdminTenderCaseSummary = {
  caseId: string
  caseTitle: string
  caseStatus: string
  latestRunId: string | null
  latestRunNumber: number | null
  extractionStatus: string | null
  bidReviewStatus: string | null
  cvMatchStatus: string | null
  runCount: number
  latestActivityAt: string | null
  derivedStatus: AdminTenderDerivedStatus
}

export type AdminTenderUsageBucket = {
  inputTokens: number
  outputTokens: number
  cachedInputTokens: number
  totalTokens: number
  llmCalls: number
}

/**
 * Token use and estimated cost for one case, aggregated from its usage records.
 *
 * Covers the pipeline steps listed in `covers` only — CV matching records no
 * usage, and case chat and Document Intelligence are metered separately — so
 * this is a floor, not the full cost of a case.
 */
export type AdminTenderUsageSummary = AdminTenderUsageBucket & {
  cost: number
  currency: string
  /**
   * "api" is live Azure pricing, "manual" means at least one rate came from the
   * static fallback table, "unknown" means at least one model could not be
   * priced at all — in which case `cost` is short by an unknown amount.
   */
  pricingSource: "api" | "scraped" | "manual" | "unknown"
  /** Models whose rate could not be resolved; their tokens are missing from `cost`. */
  unpricedModels: string[]
  /** False when `unpricedModels` is non-empty — `cost` is then a floor, not a total. */
  costComplete: boolean
  /** One per run+step+attempt. Not a retry count — a clean run of two steps is 2. */
  stepExecutions: number
  failedStepExecutions: number
  /** Steps that ran more than once within a run, i.e. actual retries. */
  retriedSteps: number
  runCount: number
  userCount: number
  covers: string[]
  byModel: Array<AdminTenderUsageBucket & { model: string; cost: number }>
  byStep: Array<AdminTenderUsageBucket & { step: string }>
  byRun: Array<AdminTenderUsageBucket & { runId: string; cost: number }>
}

export type AdminTenderCaseDetail = {
  case: {
    id: string
    title: string
    status: string
    updatedAt: string
    activeRunId: string | null
  }
  selectedRun: AdminTenderRunSummary
  runs: AdminTenderRunSummary[]
  extraction: {
    status: string | null
    startedAt: string | null
    completedAt: string | null
    error: string | null
    failureReason: string | null
    categoryStatus: Record<string, string>
    categoryErrors: Record<string, string>
    phaseMetrics: Array<Record<string, unknown>>
    pipelineVersion: string | null
  }
  bidReview: {
    status: string | null
    startedAt: string | null
    completedAt: string | null
    error: string | null
    failureReason: string | null
  }
  cvMatch: {
    status: string | null
    startedAt: string | null
    completedAt: string | null
    error: string | null
    failureReason: string | null
  }
  traceLinks: AdminTenderTraceLink[]
  /** null when the usage lookup failed — distinct from a case that cost nothing. */
  usage: AdminTenderUsageSummary | null
}

/**
 * Headline spend across all tender cases in a recent window.
 *
 * Both average and median are returned deliberately: per-case cost is
 * right-skewed, so the average alone reads as "what a case costs" while
 * overstating the typical one.
 */
export type AdminTenderUsageWindow = {
  windowDays: number
  caseCount: number
  totalCost: number
  averageCostPerCase: number
  medianCostPerCase: number
  totalTokens: number
  llmCalls: number
  currency: string
  pricingSource: "api" | "scraped" | "manual" | "unknown"
  unpricedModels: string[]
  costComplete: boolean
}

export async function getAdminTenderUsageSummary(
  days = 30,
  currency = "USD"
): Promise<AdminTenderUsageWindow> {
  const query = new URLSearchParams({ days: String(days), currency }).toString()
  const response = await api.get(`/admin/tenders/usage-summary?${query}`)
  return response.data || response
}

export type AdminTenderRunsResponse = {
  items: AdminTenderRunSummary[]
  statusFilter: "all" | AdminTenderDerivedStatus
  limit: number
}

export type AdminTenderCasesResponse = {
  items: AdminTenderCaseSummary[]
  statusFilter: "all" | AdminTenderDerivedStatus
  limit: number
}

export async function getAdminTenderRuns(
  status: "all" | AdminTenderDerivedStatus = "all",
  limit = 100
): Promise<AdminTenderRunsResponse> {
  const query = new URLSearchParams({ status, limit: String(limit) }).toString()
  const response = await api.get(`/admin/tenders/runs?${query}`)
  return response.data || response
}

export async function getAdminTenderCases(
  status: "all" | AdminTenderDerivedStatus = "all",
  limit = 50
): Promise<AdminTenderCasesResponse> {
  const query = new URLSearchParams({ status, limit: String(limit) }).toString()
  const response = await api.get(`/admin/tenders/cases?${query}`)
  return response.data || response
}

export async function getAdminTenderCaseDetail(
  caseId: string,
  runId?: string | null,
  currency?: string
): Promise<AdminTenderCaseDetail> {
  const params = new URLSearchParams()
  if (runId) params.set("run_id", runId)
  // Match the currency the rest of the admin dashboard is showing, otherwise
  // this card reports one currency next to a Costs view showing another.
  if (currency) params.set("currency", currency)
  const query = params.toString() ? `?${params.toString()}` : ""
  const response = await api.get(`/admin/tenders/cases/${caseId}/detail${query}`)
  return response.data || response
}

function normalizeAdminTraceUrl(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    const parsed = new URL(url)
    const normalizedPath = parsed.pathname.startsWith("/api/")
      ? parsed.pathname.slice(4)
      : parsed.pathname
    return `${normalizedPath}${parsed.search}`
  }

  return url.startsWith("/api/") ? url.slice(4) : url
}

export async function getAdminTenderTraceJson(url: string): Promise<unknown> {
  const response = await api.get(normalizeAdminTraceUrl(url))
  return response.data || response
}

export async function downloadAdminTenderTraceCsv(url: string): Promise<Blob> {
  const response = await api.get(normalizeAdminTraceUrl(url), { responseType: "blob" })
  return response.data || response
}

// =============================================================================
// Tender Model Configuration API
// =============================================================================

export type TenderModelKey =
  | "tender_extraction_model_id"
  | "tender_fast_model_id"
  | "tender_cv_matching_model_id"
  | "tender_bid_review_model_id"
  | "tender_chat_model_id"

export type TenderEffortKey =
  | "tender_extraction_reasoning_effort"
  | "tender_fast_reasoning_effort"
  | "tender_cv_matching_reasoning_effort"
  | "tender_bid_review_reasoning_effort"
  | "tender_chat_reasoning_effort"

export type TenderReasoningProfile = {
  tier: "current" | "legacy"
  supported_efforts: string[]
  default_effort: string
}

export type TenderModelConfig = Record<TenderModelKey | TenderEffortKey, string> & {
  // Baseline defaults with Tender mappings cleared. Bid review follows a
  // selected extraction model first; its baseline is the ordinary Auto model.
  default_model_ids: Record<TenderModelKey, string>
  reasoning_profiles: Partial<Record<string, TenderReasoningProfile>>
}

export type TenderModelUpdates = Partial<Record<TenderModelKey | TenderEffortKey, string>>

export async function getTenderModels(): Promise<TenderModelConfig> {
  const response = await api.get("/admin/tender/models")
  return response.data || response
}

export async function updateTenderModels(
  config: TenderModelUpdates
): Promise<TenderModelConfig & { success: boolean; message: string }> {
  const response = await api.put("/admin/tender/models", config)
  return response.data || response
}

// =============================================================================
// Tender Handoff Configuration API (recipient-picker group + dedicated mailbox)
// =============================================================================

// Which summary layout the hand-over email renders: "trimmed" is the reference
// group's condensed structure (default), "full" the previous, longer layout.
export type TenderHandoffSummaryLayout = "trimmed" | "full"

export type TenderHandoffConfig = {
  user_group_id?: string | null
  user_group_name?: string | null
  platform_access_group_id?: string | null
  platform_access_group_name?: string | null
  email_enabled?: boolean
  sender_email?: string | null
  // App registration: id/tenant plaintext; client_secret is a Key Vault secret
  // NAME (stored as @kv:<name>, masked as ***configured*** when set).
  client_id?: string | null
  tenant_id?: string | null
  client_secret?: string | null
  // Static bid-routines block rendered near the top of the hand-over email.
  routines_title?: string | null
  routines_text?: string | null
  // Missing/null is treated as "trimmed" by the backend.
  summary_layout?: TenderHandoffSummaryLayout | null
}

export async function getAdminTenderHandoffConfig(): Promise<{
  tender_handoff_config: TenderHandoffConfig
}> {
  const response = await api.get("/admin/tenders/handoff-config")
  return response.data || response
}

export async function updateAdminTenderHandoffConfig(
  config: TenderHandoffConfig
): Promise<{ tender_handoff_config: TenderHandoffConfig }> {
  const response = await api.put("/admin/tenders/handoff-config", config)
  return response.data || response
}

// Verify the mailbox credentials server-side (no email sent): the Key Vault secret
// exists and the app registration can obtain a Graph token. Validates the entered
// values, falling back to the stored ones when a field is left blank.
export async function verifyAdminTenderHandoffConfig(body: {
  client_id?: string | null
  tenant_id?: string | null
  client_secret?: string | null
}): Promise<{ secretValid: boolean; tokenValid: boolean; error?: string }> {
  const response = await api.post("/admin/tenders/handoff-config/verify", body)
  return response.data || response
}

// Send a test hand-over email to the calling admin from the saved mailbox config.
export async function sendAdminTenderHandoffTestEmail(): Promise<{
  sent: boolean
  recipient?: string
  error?: string
}> {
  const response = await api.post("/admin/tenders/handoff-config/test-email", {})
  return response.data || response
}
